/**
 * @feature quote-requests
 * @layer presentation/components/utils
 *
 * Data Extractor Utilities
 * Extracts normalized quote data from either form context or API response
 * Handles differences in data structure between CreateQuoteRequestDto and QuoteRequestDetailDto
 */

import type {
  QuoteReviewDataSource,
  NormalizedQuoteData,
  ExtendedQuoteData,
  ItemData,
  AttachmentMetadataDto,
  ConstructionTypeLabelValue,
  SpaceTypeLabelValue,
  ConstructionAreaLabelValue,
  QuoteRequestDetailDto,
} from "../types";
import type {
  QuoteRequestItemResponseDto,
  LabelValuePair,
} from "@semo/api-contracts";
import { parseSpecification } from "@semo/shared-utils";

// ============================================================================
// Main Data Extraction Function
// ============================================================================

/**
 * Extract normalized quote data from either form context or API response
 * Handles structural differences between the two data sources
 *
 * @param dataSource - Discriminated union (form | api)
 * @returns NormalizedQuoteData with unified structure
 */
export function extractQuoteData(
  dataSource: QuoteReviewDataSource,
): NormalizedQuoteData {
  if (dataSource.source === "form") {
    const fd = dataSource.formData;
    const isRecord = (v: unknown): v is Record<string, unknown> =>
      typeof v === "object" && v !== null && !Array.isArray(v);
    return extractFormData(isRecord(fd) ? fd : {});
  } else {
    return extractApiData(dataSource.apiData);
  }
}

// ============================================================================
// Form Data Extraction
// ============================================================================

function asType<T>(val: unknown): T | undefined {
  const isType = (_v: unknown): _v is T => true;
  return isType(val) ? val : undefined;
}

/**
 * Extract data from form context (CreateQuoteRequestDto)
 * Form data has fields at root level
 */
function extractFormData(
  formData: Record<string, unknown>,
): NormalizedQuoteData {
  return {
    projectName:
      formData.projectName != null ? String(formData.projectName) : undefined,
    categoryType:
      formData.categoryType != null ? String(formData.categoryType) : undefined,

    // Items (at root level in form)
    items: extractItems(formData.items),

    // Construction-specific (at root level in form)
    constructionType: asType<ConstructionTypeLabelValue>(
      extractOptionValue(formData.constructionType),
    ),
    spaceType: asType<SpaceTypeLabelValue>(
      extractOptionValue(formData.spaceType),
    ),
    constructionArea: asType<ConstructionAreaLabelValue>(
      extractOptionValue(formData.constructionArea),
    ),

    // Conditions & Requests
    additionalConditions: extractOptionValueArray(
      formData.additionalConditions,
    ),
    additionalRequests: extractOptionValueArray(formData.additionalRequests),

    // Description & Attachments
    description: extractDescription(formData.description),
    attachments: extractAttachments(formData.attachments),

    // Deadline
    deadline: formData.deadline != null ? String(formData.deadline) : undefined,
    dueDate: extractDateString(formData.dueDate),
    dueDateType:
      formData.dueDateType != null ? String(formData.dueDateType) : undefined,
    dueDateValue: extractDueDateValue(formData.dueDateValue),
    dueDateDays: extractNumber(formData.dueDateDays),

    // Address
    addressId:
      formData.addressId != null ? String(formData.addressId) : undefined,
    address: extractAddress(formData.address),
    sameRegionOnly: Boolean(formData.sameRegionOnly),

    // Budget
    budgetAmount: extractNumber(formData.budgetAmount),
    budgetType:
      formData.budgetType != null ? String(formData.budgetType) : undefined,

    // Manager
    managerName:
      formData.managerName != null ? String(formData.managerName) : undefined,
    managerContact:
      formData.managerContact != null
        ? String(formData.managerContact)
        : undefined,

    // Public flag
    isPublic: Boolean(formData.isPublic),
  };
}

// ============================================================================
// API Data Extraction
// ============================================================================

/**
 * Extract data from API response (QuoteRequestDetailDto)
 * ✅ Strongly typed with QuoteRequestDetailDto for full type safety
 * API data has fields both at root level and in metadata/categoryDetails
 */
function extractApiData(apiData: QuoteRequestDetailDto): NormalizedQuoteData {
  const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);
  const data: Record<string, unknown> = isRecord(apiData) ? apiData : {};

  // Extract extended data from metadata or categoryDetails (if available)
  const extendedData = extractExtendedData(
    data.metadata ?? data.categoryDetails,
  );

  return {
    projectName: String(apiData.projectName || "") || undefined,
    categoryType: String(apiData.categoryType),
    status: String(apiData.status),

    // Items (check root level first, then metadata/categoryDetails)
    items: extractItems(data.items) ?? extendedData.items,

    // Construction-specific (from root level)
    constructionType: asType<ConstructionTypeLabelValue>(
      extractOptionValue(data.constructionType),
    ),
    spaceType: asType<SpaceTypeLabelValue>(extractOptionValue(data.spaceType)),
    constructionArea: asType<ConstructionAreaLabelValue>(
      extractOptionValue(data.constructionArea),
    ),

    // Conditions & Requests (check root level first, then metadata/categoryDetails)
    additionalConditions:
      extractOptionValueArray(data.additionalConditions) ??
      extendedData.additionalConditions ??
      undefined,
    additionalRequests:
      extractOptionValueArray(data.additionalRequests) ??
      (Array.isArray(extendedData.additionalRequests)
        ? extendedData.additionalRequests
        : undefined),

    // Description & Attachments
    description: extractDescription(data.description),
    attachments: extractAttachments(data.attachments),

    // Deadline
    deadline: data.deadline != null ? String(data.deadline) : undefined,
    dueDate: extractDateString(data.dueDate),
    dueDateType: extendedData.dueDateType,
    // 상세 응답은 납기 유형(«협의가능»/«구체적»)을 최상위 dueDateValue 로 준다 — 메타데이터만
    // 보면 «계약 후 협의 가능» 으로 등록한 건이 상세에서 «미정» 으로 보였다(GS A2-30).
    dueDateValue:
      extractDueDateValue(data.dueDateValue) ?? extendedData.dueDateValue,
    dueDateDays: extendedData.dueDateDays,

    // Address
    addressId: data.addressId != null ? String(data.addressId) : undefined,
    address: extractAddress(data.address), // Extract address if present in API response
    sameRegionOnly:
      typeof data.sameRegionOnly === "boolean"
        ? data.sameRegionOnly
        : undefined,

    // Budget
    budgetAmount: extractNumber(data.budgetAmount),
    budgetType: data.budgetType != null ? String(data.budgetType) : undefined, // Convert null to undefined
    budgetOption: undefined, // Not in API response

    // Manager
    organizationName:
      data.organizationName != null && String(data.organizationName).trim()
        ? String(data.organizationName)
        : undefined,
    managerName:
      data.managerName != null ? String(data.managerName) : undefined,
    managerContact:
      data.managerContact != null ? String(data.managerContact) : undefined,

    // Public flag
    isPublic: Boolean(data.isPublic),
  };
}

// ============================================================================
// Extended Data Extraction (from metadata/categoryDetails)
// ============================================================================

/**
 * Extract extended data from metadata or categoryDetails object
 */
function extractExtendedData(metadata: unknown): ExtendedQuoteData {
  if (!metadata || typeof metadata !== "object") {
    return {};
  }

  const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);
  const data: Record<string, unknown> = isRecord(metadata) ? metadata : {};

  return {
    items: extractItems(data.items),
    addressId: data.addressId != null ? String(data.addressId) : undefined,
    constructionType: data.constructionType
      ? asType<ConstructionTypeLabelValue>(
          extractOptionValue(data.constructionType),
        )
      : undefined,
    spaceType: data.spaceType
      ? asType<SpaceTypeLabelValue>(extractOptionValue(data.spaceType))
      : undefined,
    constructionArea: data.constructionArea
      ? asType<ConstructionAreaLabelValue>(
          extractOptionValue(data.constructionArea),
        )
      : undefined,
    additionalRequests: extractOptionValueArray(data.additionalRequests),
    additionalConditions: extractOptionValueArray(data.additionalConditions),
    dueDateType:
      data.dueDateType != null ? String(data.dueDateType) : undefined,
    dueDateValue: extractDueDateValue(data.dueDateValue),
    dueDateDays: extractNumber(data.dueDateDays),
  };
}

// ============================================================================
// Field Extraction Helpers
// ============================================================================

/**
 * Extended item DTO including standard item fields
 */
interface ExtendedQuoteRequestItemDto extends QuoteRequestItemResponseDto {
  itemId?: string;
  itemName?: string;
  itemCode?: string;
  itemCodeMatch?: string;
  itemPrice?: number;
  itemImageUrl?: string;
  itemSpec?: string;
  specification?: string;
  spec?: string;
}

/**
 * Type guard for QuoteRequestItemResponseDto with optional standard item fields
 */
function isExtendedQuoteRequestItemDto(
  item: unknown,
): item is ExtendedQuoteRequestItemDto {
  return (
    typeof item === "object" &&
    item !== null &&
    "name" in item &&
    "quantity" in item
  );
}

/**
 * Extract items array with proper Orval typing
 * ✅ Strongly typed with QuoteRequestItemResponseDto for full type safety
 * Handles both API (QuoteRequestItemResponseDto[]) and form structures
 * Preserves spec1, spec2, spec3 from API response for proper spec combination
 * For form data, parses specification field into spec1/spec2/spec3
 */
function extractItems(items: unknown): ItemData[] | undefined {
  if (!Array.isArray(items)) return undefined;

  return items.filter(isExtendedQuoteRequestItemDto).map((item) => {
    // Form 데이터의 specification을 spec1/spec2/spec3로 파싱 (API 데이터는 이미 분리됨)
    const parsedSpecs = item.spec1
      ? {}
      : parseSpecification(item.specification);

    return {
      name: item.name,
      // Note: productName only exists in form data, not in QuoteRequestItemResponseDto API response
      productName: undefined,
      itemId: item.itemId,
      itemName: item.itemName,
      itemCode: item.itemCode,
      itemCodeMatch: item.itemCodeMatch,
      itemPrice: item.itemPrice,
      itemImageUrl: item.itemImageUrl,
      itemSpec: item.itemSpec,
      // API spec 필드 우선, 없으면 파싱된 specification 사용
      spec1: item.spec1 || parsedSpecs.spec1,
      spec2: item.spec2 || parsedSpecs.spec2,
      spec3: item.spec3 || parsedSpecs.spec3,
      specification: item.specification,
      spec: item.spec,
      quantity: item.quantity,
      unit: item.unit,
    };
  });
}

/**
 * Extract address data safely
 */
function extractAddress(address: unknown): Record<string, unknown> | undefined {
  if (!address || typeof address !== "object") return undefined;

  const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);
  if (!isRecord(address)) return undefined;

  const obj = address;
  return {
    id: obj.id != null ? String(obj.id) : undefined,
    label: obj.label != null ? String(obj.label) : undefined,
    addressAlias:
      (obj.addressAlias ?? obj.alias) != null
        ? String(obj.addressAlias ?? obj.alias)
        : undefined,
    province: obj.province != null ? String(obj.province) : undefined,
    city: obj.city != null ? String(obj.city) : undefined,
    district: obj.district != null ? String(obj.district) : undefined,
    // 견적 요청 상세 응답은 도로명·상세를 addressLine1·addressLine2 로 준다(본인·어드민만).
    roadAddress:
      (obj.roadAddress ?? obj.address ?? obj.addressLine1) != null
        ? String(obj.roadAddress ?? obj.address ?? obj.addressLine1)
        : undefined,
    streetAddress:
      obj.streetAddress != null ? String(obj.streetAddress) : undefined,
    addressDetail:
      (obj.addressDetail ?? obj.addressLine2) != null
        ? String(obj.addressDetail ?? obj.addressLine2)
        : undefined,
    postalCode: obj.postalCode != null ? String(obj.postalCode) : undefined,
    recipientName:
      obj.recipientName != null ? String(obj.recipientName) : undefined,
    recipientPhone:
      obj.recipientPhone != null ? String(obj.recipientPhone) : undefined,
  };
}

/**
 * Extract description, normalizing whitespace
 */
function extractDescription(description: unknown): string | undefined {
  if (typeof description !== "string") return undefined;

  // Remove newlines and normalize spacing
  return (
    description.replace(/\n/g, "").replace(/>\s+</g, "><").trim() || undefined
  );
}

/**
 * Extract attachments array
 * AttachmentMetadataDto has: { fileName: string, url: string, fileSize: number, contentType?: string }
 */
function extractAttachments(
  attachments: unknown,
): AttachmentMetadataDto[] | undefined {
  if (!Array.isArray(attachments)) return undefined;

  const validAttachments = attachments.filter(
    (attachment): attachment is AttachmentMetadataDto => {
      if (typeof attachment !== "object" || !attachment) return false;

      const isRecord = (v: unknown): v is Record<string, unknown> =>
        typeof v === "object" && v !== null && !Array.isArray(v);
      if (!isRecord(attachment)) return false;

      return (
        typeof attachment.fileName === "string" &&
        typeof attachment.url === "string"
      );
    },
  );

  return validAttachments.length > 0 ? validAttachments : undefined;
}

/**
 * Extract option value (single select)
 * Handles both { value, label } and string formats
 *
 * @returns LabelValuePair (Swagger-generated type) or undefined
 */
function extractOptionValue(option: unknown): LabelValuePair | undefined {
  if (!option) return undefined;

  const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

  if (isRecord(option)) {
    if (typeof option.value === "string" && typeof option.label === "string") {
      return { value: option.value, label: option.label };
    }
  }

  if (typeof option === "string") {
    return { value: option, label: option };
  }

  return undefined;
}

/**
 * Extract option value array (multi-select)
 *
 * @returns Array of LabelValuePair (Swagger-generated type) or undefined
 */
function extractOptionValueArray(
  options: unknown,
): LabelValuePair[] | undefined {
  if (!Array.isArray(options)) return undefined;

  const extracted = options
    .map((option) => extractOptionValue(option))
    .filter((value): value is LabelValuePair => value !== undefined);

  return extracted.length > 0 ? extracted : undefined;
}

/**
 * Extract number safely
 */
function extractNumber(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;

  const num = Number(value);
  return !isNaN(num) ? num : undefined;
}

/**
 * Extract date string from various formats
 * Handles: string, Date object, { value: string }, null, undefined
 */
function extractDateString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;

  // Already a string
  if (typeof value === "string") {
    return value || undefined;
  }

  // Date object
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Object with value property (e.g., { value: '2024-12-25' })
  if (typeof value === "object") {
    const isRecord = (v: unknown): v is Record<string, unknown> =>
      typeof v === "object" && v !== null && !Array.isArray(v);
    if (isRecord(value)) {
      if (typeof value.value === "string") {
        return value.value || undefined;
      }
    }
    // Object with toISOString method (Date-like)
    const toIso = Reflect.get(value, "toISOString");
    if (typeof toIso === "function") {
      return toIso.call(value);
    }
  }

  return undefined;
}

/**
 * Extract due date value from various formats
 * Handles: string, { value: string }, { label: string }, null, undefined
 */
function extractDueDateValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;

  // Already a string
  if (typeof value === "string") {
    return value || undefined;
  }

  // Object with value property (e.g., { value: '협의가능' })
  if (typeof value === "object") {
    const isRecord = (v: unknown): v is Record<string, unknown> =>
      typeof v === "object" && v !== null && !Array.isArray(v);
    if (isRecord(value)) {
      if (typeof value.value === "string") {
        return value.value || undefined;
      }
      if (typeof value.label === "string") {
        return value.label || undefined;
      }
    }
  }

  return undefined;
}

// ============================================================================
// Data Validation Helpers
// ============================================================================

/**
 * Check if data has meaningful content (for conditional rendering)
 */
export function hasContent(value: unknown): boolean {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  if (typeof value === "number") return true;
  if (typeof value === "boolean") return value;

  return false;
}

/**
 * Check if address data is complete
 */
export function isAddressComplete(
  address: Record<string, unknown> | undefined,
): boolean {
  if (!address) return false;

  return (
    hasContent(address.roadAddress ?? address.address) &&
    (hasContent(address.recipientName) || hasContent(address.recipientPhone))
  );
}

/**
 * Check if items array has valid data
 */
export function hasValidItems(items: ItemData[] | undefined): boolean {
  if (!Array.isArray(items)) return false;

  return items.some(
    (item) =>
      hasContent(item.name ?? item.productName) || hasContent(item.quantity),
  );
}
